import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getUpcomingEvents,
  getVolunteerOpportunities,
  getVolunteerSignup,
  signUpAsVolunteer,
  withdrawVolunteerSignup,
  getVolunteerAllEventRegistrations,
  registerForEvent,
  cancelRegistration,
  generateICS,
  fmtDateTime,
  daysUntil,
  EVENT_CATEGORIES,
} from '../../../api/eventsApi';
import {
  submitEventProposal,
  getVolunteerProposals,
} from '../../../api/eventProposalsApi';
import {
  estimateAudience,
} from '../../../api/eventsApi';
import {
  SINGAPORE_DISTRICTS,
  MEMBER_INTERESTS,
  MEMBER_EMPLOYMENT_STATUSES,
} from '../../../api/communityApi';

/* ─── Constants ──────────────────────────────────────────────── */
const CATEGORY_COLORS = {
  CIRCLE_TIME:          '#0D9488',
  TALK_WORKSHOP:        '#4338CA',
  SOCIAL_RECREATIONAL:  '#F97316',
  OTHER:                '#6B7280',
};

const STATUS_BADGE = {
  PUBLISHED: 'bb-badge bb-badge--green',
  COMPLETED: 'bb-badge bb-badge--blue',
};

const PROPOSAL_STATUS_BADGE = {
  PROPOSAL_PENDING:  { label: 'Under Review', cls: 'bb-badge bb-badge--orange' },
  PROPOSAL_APPROVED: { label: 'Approved ✓',   cls: 'bb-badge bb-badge--green' },
  PROPOSAL_DECLINED: { label: 'Declined',     cls: 'bb-badge bb-badge--red'   },
};

const EMPTY_PROPOSAL = {
  title: '', category: 'CIRCLE_TIME', event_type: 'ONE_OFF',
  start_datetime: '', end_datetime: '',
  location: { name: '', address: '', postal_code: '' },
  member_capacity: '', volunteer_capacity: '',
  description: '', tags: '',
  photo_consent_required: false,
  volunteers_required: false, volunteers_needed: '',
};

function categoryLabel(value) {
  const cat = EVENT_CATEGORIES.find(c => c.value === value);
  return cat ? cat.label : value;
}

/* ─── Calendar helpers ────────────────────────────────────────── */
function toCalDate(val) {
  return val?.toDate ? val.toDate() : new Date(val);
}

function fmtICS(d) {
  return d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
}

function downloadICS(event) {
  const ics = generateICS(event);
  const blob = new Blob([ics], { type: 'text/calendar' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${event.title.replace(/[^a-z0-9]/gi, '_')}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function buildGoogleCalendarUrl(event) {
  const start = toCalDate(event.start_datetime);
  const end   = toCalDate(event.end_datetime);
  const params = new URLSearchParams({
    action:   'TEMPLATE',
    text:     event.title || '',
    dates:    `${fmtICS(start)}/${fmtICS(end)}`,
    details:  event.description || '',
    location: [event.location?.name, event.location?.address].filter(Boolean).join(', '),
  });
  return `https://calendar.google.com/calendar/render?${params}`;
}

function buildOutlookUrl(event) {
  const start = toCalDate(event.start_datetime);
  const end   = toCalDate(event.end_datetime);
  const params = new URLSearchParams({
    path:     '/calendar/action/compose',
    rru:      'addevent',
    subject:  event.title || '',
    startdt:  start.toISOString(),
    enddt:    end.toISOString(),
    body:     event.description || '',
    location: [event.location?.name, event.location?.address].filter(Boolean).join(', '),
  });
  return `https://outlook.live.com/calendar/0/action/compose?${params}`;
}

/* ─── Add to Calendar button with provider picker ─────────────── */
function AddToCalendarButton({ event }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const options = [
    {
      label: 'Google Calendar',
      icon: '🗓',
      action: () => { window.open(buildGoogleCalendarUrl(event), '_blank', 'noopener,noreferrer'); setOpen(false); },
    },
    {
      label: 'Outlook',
      icon: '📧',
      action: () => { window.open(buildOutlookUrl(event), '_blank', 'noopener,noreferrer'); setOpen(false); },
    },
    {
      label: 'Apple / Other (.ics)',
      icon: '⬇️',
      action: () => { downloadICS(event); setOpen(false); },
    },
  ];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="bb-btn-outline-sm"
        style={{ borderColor: '#4338CA', color: '#4338CA', fontSize: 11, padding: '3px 8px' }}
        onClick={() => setOpen(v => !v)}
      >
        📅 Add to Calendar
      </button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '100%', marginTop: 4,
          background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)', zIndex: 100, minWidth: 190, overflow: 'hidden',
        }}>
          {options.map(opt => (
            <button
              key={opt.label}
              onClick={opt.action}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                width: '100%', padding: '9px 14px', background: 'none',
                border: 'none', cursor: 'pointer', fontSize: 13, color: '#111827',
                textAlign: 'left', whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#F3F4F6'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              <span>{opt.icon}</span>
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Section header ───────────────────────────────────────────── */
function SectionHeader({ title, count }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
      <span style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>{title}</span>
      <span style={{ fontSize: 12, color: '#6B7280', background: '#F3F4F6', borderRadius: 10, padding: '1px 8px' }}>{count}</span>
    </div>
  );
}

/* ─── Tab: All Upcoming Events ─────────────────────────────────── */
function AllUpcomingEvents({ orgId, volunteerId }) {
  const [events, setEvents] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  useEffect(() => {
    setLoading(true); setError('');
    getUpcomingEvents(orgId, 90)
      .then(data => { setEvents(data); setFiltered(data); })
      .catch(() => setError('Failed to load upcoming events.'))
      .finally(() => setLoading(false));
  }, [orgId]);

  useEffect(() => {
    setFiltered(categoryFilter ? events.filter(e => e.category === categoryFilter) : events);
  }, [categoryFilter, events]);

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><div className="bb-loading-spinner" /></div>;
  if (error)   return <div className="bb-alert bb-alert--error">{error}</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>Filter by category:</label>
        <select className="bb-select" style={{ maxWidth: 240 }} value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
          <option value="">All Categories</option>
          {EVENT_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        {categoryFilter && <button style={{ fontSize: 12, color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} onClick={() => setCategoryFilter('')}>Clear</button>}
      </div>
      {filtered.length === 0 ? (
        <div className="bb-empty-state">{categoryFilter ? `No upcoming events in "${categoryLabel(categoryFilter)}".` : 'No upcoming events in the next 90 days.'}</div>
      ) : (
        filtered.map(evt => {
          const isAssigned = (evt.assigned_volunteers || []).includes(volunteerId);
          const catColor = CATEGORY_COLORS[evt.category] || '#6B7280';
          const days = daysUntil(evt.start_datetime);
          return (
            <div key={evt.id} className="bb-vol-card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
              <div style={{ background: catColor, height: 6 }} />
              <div style={{ padding: '14px 18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: catColor, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{categoryLabel(evt.category)}</span>
                      <span className={STATUS_BADGE[evt.status] || 'bb-badge bb-badge--grey'}>{evt.status}</span>
                      {isAssigned && <span className="bb-badge bb-badge--blue">Assigned</span>}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#111827', marginBottom: 4 }}>{evt.title}</div>
                    <div style={{ fontSize: 13, color: '#4B5563', marginBottom: 2 }}>{fmtDateTime(evt.start_datetime)}{evt.end_datetime && ` – ${fmtDateTime(evt.end_datetime)}`}</div>
                    {evt.location?.name && <div style={{ fontSize: 13, color: '#6B7280' }}>📍 {evt.location.name}{evt.location.address ? `, ${evt.location.address}` : ''}</div>}
                    {evt.description && <div style={{ fontSize: 12, color: '#6B7280', marginTop: 6, maxWidth: 560 }}>{evt.description.length > 120 ? evt.description.slice(0, 120) + '…' : evt.description}</div>}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                    {days !== null && days > 0 && <span style={{ fontSize: 11, color: '#6B7280' }}>in {days}d</span>}
                    <AddToCalendarButton event={evt} />
                  </div>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

/* ─── Tab: Volunteer Opportunities (role sign-ups) ─────────────── */
function VolunteerOpportunities({ orgId, volunteerId, volunteerName }) {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [signupStatus, setSignupStatus] = useState({});
  const [actionMsg, setActionMsg] = useState('');

  const showMsg = (msg) => { setActionMsg(msg); setTimeout(() => setActionMsg(''), 3000); };

  const loadOpportunities = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const events = await getVolunteerOpportunities(orgId);
      const statuses = {};
      await Promise.all(events.map(async (evt) => {
        try {
          const signup = await getVolunteerSignup(orgId, evt.id, volunteerId);
          statuses[evt.id] = signup ? 'signed_up' : null;
        } catch { statuses[evt.id] = null; }
      }));
      setOpportunities(events);
      setSignupStatus(statuses);
    } catch { setError('Failed to load volunteer opportunities.'); }
    finally { setLoading(false); }
  }, [orgId, volunteerId]);

  useEffect(() => { loadOpportunities(); }, [loadOpportunities]);

  const handleSignUp = async (evt) => {
    setSignupStatus(p => ({ ...p, [evt.id]: 'loading' }));
    try {
      await signUpAsVolunteer(orgId, evt.id, volunteerId, volunteerName);
      setSignupStatus(p => ({ ...p, [evt.id]: 'signed_up' }));
      setOpportunities(p => p.map(e => e.id === evt.id ? { ...e, volunteer_signup_count: (e.volunteer_signup_count || 0) + 1 } : e));
      showMsg(`You've signed up to volunteer for "${evt.title}"!`);
    } catch (e) { setSignupStatus(p => ({ ...p, [evt.id]: null })); setError(e.message || 'Sign-up failed.'); }
  };

  const handleWithdraw = async (evt) => {
    if (!window.confirm(`Withdraw your volunteer sign-up for "${evt.title}"?`)) return;
    setSignupStatus(p => ({ ...p, [evt.id]: 'loading' }));
    try {
      await withdrawVolunteerSignup(orgId, evt.id, volunteerId);
      setSignupStatus(p => ({ ...p, [evt.id]: null }));
      setOpportunities(p => p.map(e => e.id === evt.id ? { ...e, volunteer_signup_count: Math.max((e.volunteer_signup_count || 1) - 1, 0) } : e));
      showMsg(`You've withdrawn your volunteer sign-up for "${evt.title}".`);
    } catch (e) { setSignupStatus(p => ({ ...p, [evt.id]: 'signed_up' })); setError(e.message || 'Withdrawal failed.'); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><div className="bb-loading-spinner" /></div>;

  return (
    <div>
      {error    && <div className="bb-alert bb-alert--error" style={{ marginBottom: 12 }}>{error}</div>}
      {actionMsg && <div className="bb-alert bb-alert--success" style={{ marginBottom: 12 }}>{actionMsg}</div>}
      <SectionHeader title="Available Events for Volunteering" count={opportunities.length} />
      {opportunities.length === 0 ? (
        <div className="bb-empty-state">No volunteer opportunities are available right now. Check back soon!</div>
      ) : (
        opportunities.map(evt => {
          const status   = signupStatus[evt.id];
          const isFull   = (evt.volunteer_signup_count || 0) >= (evt.volunteers_needed || 0);
          const signedUp = status === 'signed_up';
          const busy     = status === 'loading';
          const catColor = CATEGORY_COLORS[evt.category] || '#6B7280';
          const days     = daysUntil(evt.start_datetime);
          return (
            <div key={evt.id} className="bb-vol-card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
              <div style={{ background: catColor, height: 6 }} />
              <div style={{ padding: '14px 18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: catColor, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{categoryLabel(evt.category)}</span>
                      {signedUp && <span className="bb-badge bb-badge--green">You're Signed Up</span>}
                      {isFull && !signedUp && <span className="bb-badge bb-badge--grey">Full</span>}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#111827', marginBottom: 4 }}>{evt.title}</div>
                    <div style={{ fontSize: 13, color: '#4B5563', marginBottom: 2 }}>{fmtDateTime(evt.start_datetime)}{evt.end_datetime && ` – ${fmtDateTime(evt.end_datetime)}`}</div>
                    {evt.location?.name && <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 6 }}>📍 {evt.location.name}{evt.location.address ? `, ${evt.location.address}` : ''}</div>}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                      <span style={{ fontSize: 12, color: '#6B7280' }}>Volunteers needed:</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: isFull ? '#059669' : '#92400E' }}>{evt.volunteer_signup_count || 0} / {evt.volunteers_needed || 0}</span>
                      <div style={{ flex: 1, maxWidth: 100, height: 6, background: '#E5E7EB', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: 3, background: isFull ? '#059669' : '#6B46C1', width: `${Math.min(((evt.volunteer_signup_count || 0) / (evt.volunteers_needed || 1)) * 100, 100)}%`, transition: 'width 0.3s' }} />
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
                    {days !== null && days > 0 && <span style={{ fontSize: 11, color: '#6B7280' }}>in {days}d</span>}
                    {signedUp ? (
                      <button className="bb-btn-danger-sm" style={{ padding: '6px 14px' }} onClick={() => handleWithdraw(evt)} disabled={busy}>{busy ? '…' : 'Withdraw'}</button>
                    ) : (
                      <button className="bb-btn-primary" style={{ padding: '6px 14px', fontSize: 13, opacity: isFull ? 0.5 : 1 }} onClick={() => handleSignUp(evt)} disabled={busy || isFull}>
                        {busy ? 'Signing up…' : isFull ? 'No Spots Left' : 'Volunteer for this Event'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

/* ─── Tab: Register for Events (as attendee) ───────────────────── */
function RegisterForEvents({ orgId, volunteerId, volunteerName, volunteerEmail }) {
  const [upcoming, setUpcoming]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [regStatus, setRegStatus]       = useState({});   // eventId → 'registered'|'waitlisted'|'loading'|null
  const [regData, setRegData]           = useState({});   // eventId → registration doc
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [msg, setMsg]   = useState('');
  const [error, setError] = useState('');

  const showMsg   = (m) => { setMsg(m);   setTimeout(() => setMsg(''), 3500); };
  const showError = (m) => { setError(m); setTimeout(() => setError(''), 5000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [events, allRegs] = await Promise.all([
        getUpcomingEvents(orgId, 90),
        getVolunteerAllEventRegistrations(orgId, volunteerId),
      ]);
      setUpcoming(events);

      const statusMap = {};
      const dataMap   = {};
      for (const reg of allRegs) {
        if (reg.status !== 'CANCELLED') {
          statusMap[reg.event_id] = reg.status === 'WAITLISTED' ? 'waitlisted' : 'registered';
          dataMap[reg.event_id]   = reg;
        }
      }
      setRegStatus(statusMap);
      setRegData(dataMap);
    } catch (e) { showError('Failed to load events.'); }
    finally { setLoading(false); }
  }, [orgId, volunteerId]);

  useEffect(() => { load(); }, [load]);

  const handleRegister = async (evt) => {
    const cap = evt.volunteer_capacity;
    if (cap != null && cap === 0) { showError('This event does not accept volunteer attendees.'); return; }
    setRegStatus(p => ({ ...p, [evt.id]: 'loading' }));
    try {
      const result = await registerForEvent(orgId, evt.id, volunteerId, volunteerName, volunteerEmail, volunteerId, null, 'volunteer');
      setRegStatus(p => ({ ...p, [evt.id]: result.status === 'WAITLISTED' ? 'waitlisted' : 'registered' }));
      setRegData(p => ({ ...p, [evt.id]: result }));
      showMsg(result.status === 'WAITLISTED'
        ? `You've been added to the waitlist for "${evt.title}".`
        : `You're registered for "${evt.title}"!`
      );
    } catch (e) { setRegStatus(p => ({ ...p, [evt.id]: null })); showError(e.message || 'Registration failed.'); }
  };

  const openCancel = (evt) => { setCancelTarget(evt); setCancelReason(''); };

  const handleCancel = async () => {
    if (!cancelTarget || !cancelReason.trim()) return;
    setCancelLoading(true);
    const reg = regData[cancelTarget.id];
    try {
      await cancelRegistration(orgId, cancelTarget.id, reg.id, cancelReason, volunteerId);
      setRegStatus(p => { const n = { ...p }; delete n[cancelTarget.id]; return n; });
      setRegData(p => { const n = { ...p }; delete n[cancelTarget.id]; return n; });
      setCancelTarget(null); setCancelReason('');
      showMsg(`Registration for "${cancelTarget.title}" cancelled.`);
    } catch (e) { showError(e.message || 'Cancellation failed.'); }
    finally { setCancelLoading(false); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><div className="bb-loading-spinner" /></div>;

  return (
    <div>
      {error && <div className="bb-alert bb-alert--error" style={{ marginBottom: 12 }}>{error}</div>}
      {msg   && <div className="bb-alert bb-alert--success" style={{ marginBottom: 12 }}>{msg}</div>}

      <SectionHeader title="Available Events for Registration" count={upcoming.filter(e => e.volunteer_capacity != null && e.volunteer_capacity > 0).length} />
      <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 20 }}>
        Register as an <strong>attendee</strong> at upcoming events. This is separate from your volunteer role sign-ups.
        Only events with volunteer attendee slots are shown here.
      </p>

      {/* Only show events where volunteer_capacity has been explicitly set to > 0 */}
      {upcoming.filter(e => e.volunteer_capacity != null && e.volunteer_capacity > 0).length === 0 ? (
        <div className="bb-empty-state">No upcoming events with volunteer attendee slots in the next 90 days.</div>
      ) : (
        upcoming.filter(e => e.volunteer_capacity != null && e.volunteer_capacity > 0).map(evt => {
          const status   = regStatus[evt.id];
          const isLoading = status === 'loading';
          const registered = status === 'registered';
          const waitlisted = status === 'waitlisted';
          const catColor   = CATEGORY_COLORS[evt.category] || '#6B7280';
          const days        = daysUntil(evt.start_datetime);
          const volCap = evt.volunteer_capacity;

          return (
            <div key={evt.id} className="bb-vol-card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
              <div style={{ background: catColor, height: 6 }} />
              <div style={{ padding: '14px 18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: catColor, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{categoryLabel(evt.category)}</span>
                      {registered && <span className="bb-badge bb-badge--green">Registered</span>}
                      {waitlisted && <span className="bb-badge bb-badge--orange">On Waitlist</span>}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#111827', marginBottom: 4 }}>{evt.title}</div>
                    <div style={{ fontSize: 13, color: '#4B5563', marginBottom: 2 }}>{fmtDateTime(evt.start_datetime)}{evt.end_datetime && ` – ${fmtDateTime(evt.end_datetime)}`}</div>
                    {evt.location?.name && <div style={{ fontSize: 13, color: '#6B7280' }}>📍 {evt.location.name}{evt.location.address ? `, ${evt.location.address}` : ''}</div>}
                    {volCap != null && (
                      <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                        Volunteer attendee slots: <strong style={{ color: '#4338CA' }}>{volCap}</strong>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                    {days !== null && days > 0 && <span style={{ fontSize: 11, color: '#6B7280' }}>in {days}d</span>}
                    {(registered || waitlisted) ? (
                      <button className="bb-btn-danger-sm" style={{ padding: '6px 14px' }} onClick={() => openCancel(evt)} disabled={isLoading}>
                        {waitlisted ? 'Leave Waitlist' : 'Cancel Registration'}
                      </button>
                    ) : (
                      <button
                        className="bb-btn-primary"
                        style={{ padding: '6px 14px', fontSize: 13 }}
                        onClick={() => handleRegister(evt)}
                        disabled={isLoading}
                      >
                        {isLoading ? 'Registering…' : 'Register as Attendee'}
                      </button>
                    )}
                    {registered && regData[evt.id]?.qr_code_token && (
                      <a href={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(regData[evt.id].qr_code_token)}&margin=10`}
                        target="_blank" rel="noreferrer"
                        style={{ fontSize: 11, color: '#4338CA' }}>
                        View QR Code
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })
      )}

      {/* Cancel modal */}
      {cancelTarget && (
        <div className="bb-modal-overlay">
          <div className="bb-modal">
            <h3>Cancel Registration</h3>
            <p>Cancel your registration for <strong>{cancelTarget.title}</strong>? Please give a reason:</p>
            <textarea
              className="bb-textarea" rows={3} style={{ width: '100%', marginTop: 8 }}
              value={cancelReason} onChange={e => setCancelReason(e.target.value)}
              placeholder="Reason for cancellation..."
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
              <button className="bb-btn-outline-sm" onClick={() => setCancelTarget(null)}>Back</button>
              <button className="bb-btn-danger-sm" style={{ padding: '8px 16px' }} onClick={handleCancel}
                disabled={cancelLoading || !cancelReason.trim()}>
                {cancelLoading ? 'Cancelling…' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

/* ─── Audience Estimation panel (reused in proposal form) ─────── */
function AudienceEstimation({ orgId }) {
  const [open, setOpen]               = useState(false);
  const [districts, setDistricts]     = useState([]);
  const [interests, setInterests]     = useState([]);
  const [empStatuses, setEmpStatuses] = useState([]);
  const [result, setResult]           = useState(null);
  const [loading, setLoading]         = useState(false);

  const toggleItem = (list, setList, val) => {
    setList(list.includes(val) ? list.filter(v => v !== val) : [...list, val]);
    setResult(null);
  };

  const handleEstimate = async () => {
    setLoading(true);
    try {
      const r = await estimateAudience(orgId, {
        districts,
        interests,
        employmentStatuses: empStatuses,
      });
      setResult(r);
    } catch { /* non-critical */ }
    finally { setLoading(false); }
  };

  return (
    <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: 14, marginBottom: 16 }}>
      <button
        type="button"
        style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, color: '#92400E', padding: 0, display: 'flex', alignItems: 'center', gap: 6 }}
        onClick={() => setOpen(v => !v)}
      >
        📊 Audience Estimation {open ? '▲' : '▼'}
        <span style={{ fontWeight: 400, fontSize: 12, color: '#6B7280', marginLeft: 4 }}>
          — filter your member base to gauge potential attendance
        </span>
      </button>

      {open && (
        <div style={{ marginTop: 14 }}>
          {/* Districts */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 12, color: '#374151', marginBottom: 6 }}>Districts (any match)</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(SINGAPORE_DISTRICTS || []).map(d => (
                <button
                  key={d} type="button"
                  onClick={() => toggleItem(districts, setDistricts, d)}
                  style={{
                    padding: '3px 10px', borderRadius: 12, fontSize: 11, cursor: 'pointer',
                    background: districts.includes(d) ? '#F59E0B' : '#FEF3C7',
                    color:      districts.includes(d) ? '#fff'    : '#92400E',
                    border:     `1px solid ${districts.includes(d) ? '#F59E0B' : '#FDE68A'}`,
                    fontWeight: districts.includes(d) ? 600 : 400,
                  }}
                >{d}</button>
              ))}
            </div>
          </div>

          {/* Interests */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 12, color: '#374151', marginBottom: 6 }}>Interests (any match)</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(MEMBER_INTERESTS || []).map(i => (
                <button
                  key={i.value} type="button"
                  onClick={() => toggleItem(interests, setInterests, i.value)}
                  style={{
                    padding: '3px 10px', borderRadius: 12, fontSize: 11, cursor: 'pointer',
                    background: interests.includes(i.value) ? '#F59E0B' : '#FEF3C7',
                    color:      interests.includes(i.value) ? '#fff'    : '#92400E',
                    border:     `1px solid ${interests.includes(i.value) ? '#F59E0B' : '#FDE68A'}`,
                    fontWeight: interests.includes(i.value) ? 600 : 400,
                  }}
                >{i.label}</button>
              ))}
            </div>
          </div>

          {/* Employment status */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontWeight: 600, fontSize: 12, color: '#374151', marginBottom: 6 }}>Employment Status (any match)</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(MEMBER_EMPLOYMENT_STATUSES || []).map(s => (
                <button
                  key={s.value} type="button"
                  onClick={() => toggleItem(empStatuses, setEmpStatuses, s.value)}
                  style={{
                    padding: '3px 10px', borderRadius: 12, fontSize: 11, cursor: 'pointer',
                    background: empStatuses.includes(s.value) ? '#F59E0B' : '#FEF3C7',
                    color:      empStatuses.includes(s.value) ? '#fff'    : '#92400E',
                    border:     `1px solid ${empStatuses.includes(s.value) ? '#F59E0B' : '#FDE68A'}`,
                    fontWeight: empStatuses.includes(s.value) ? 600 : 400,
                  }}
                >{s.label}</button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="bb-btn-secondary"
              style={{ fontSize: 13, padding: '6px 16px' }}
              onClick={handleEstimate}
              disabled={loading}
            >
              {loading ? 'Estimating…' : 'Estimate Audience'}
            </button>
            {(districts.length > 0 || interests.length > 0 || empStatuses.length > 0) && (
              <button
                type="button"
                style={{ fontSize: 12, color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                onClick={() => { setDistricts([]); setInterests([]); setEmpStatuses([]); setResult(null); }}
              >
                Clear all
              </button>
            )}
            {result && (
              <div style={{ background: '#fff', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 16px', fontSize: 14 }}>
                {districts.length === 0 && interests.length === 0 && empStatuses.length === 0 ? (
                  <span>Total members in your org: <strong>{result.total}</strong></span>
                ) : (
                  <span>
                    Estimated audience: <strong style={{ color: '#D97706', fontSize: 18 }}>{result.filtered}</strong>
                    <span style={{ color: '#6B7280', fontSize: 12, marginLeft: 6 }}>out of {result.total} total members</span>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Tab: Propose an Event ─────────────────────────────────────── */
function ProposeEvent({ orgId, volunteerId, volunteerName }) {
  const [proposals, setProposals] = useState([]);
  const [loadingProposals, setLoadingProposals] = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState(EMPTY_PROPOSAL);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg]   = useState('');
  const [error, setError] = useState('');

  const showMsg   = (m) => { setMsg(m);   setTimeout(() => setMsg(''), 4000); };
  const showError = (m) => { setError(m); setTimeout(() => setError(''), 6000); };

  const setF  = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setLoc = (k, v) => setForm(p => ({ ...p, location: { ...p.location, [k]: v } }));

  const loadProposals = useCallback(async () => {
    setLoadingProposals(true);
    try { setProposals(await getVolunteerProposals(orgId, volunteerId)); }
    catch { /* non-critical */ }
    finally { setLoadingProposals(false); }
  }, [orgId, volunteerId]);

  useEffect(() => { loadProposals(); }, [loadProposals]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const proposalData = {
        ...form,
        member_capacity:    form.member_capacity    ? parseInt(form.member_capacity, 10)    : null,
        volunteer_capacity: form.volunteer_capacity ? parseInt(form.volunteer_capacity, 10) : null,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        volunteers_needed: form.volunteers_needed ? parseInt(form.volunteers_needed, 10) : null,
        start_datetime: form.start_datetime ? new Date(form.start_datetime) : null,
        end_datetime:   form.end_datetime   ? new Date(form.end_datetime)   : null,
      };
      await submitEventProposal(orgId, volunteerId, volunteerName, proposalData);
      setForm(EMPTY_PROPOSAL);
      setShowForm(false);
      showMsg('Your event proposal has been submitted for admin review!');
      loadProposals();
    } catch (e) { showError(e.message || 'Submission failed.'); }
    finally { setSubmitting(false); }
  };

  const fmtProposalDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-SG', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div>
      {error && <div className="bb-alert bb-alert--error" style={{ marginBottom: 12 }}>{error}</div>}
      {msg   && <div className="bb-alert bb-alert--success" style={{ marginBottom: 12 }}>{msg}</div>}

      {!showForm && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <p style={{ margin: 0, fontSize: 13, color: '#6B7280' }}>
                Have an event idea? Submit a proposal and an admin will review it.
              </p>
            </div>
            <button className="bb-btn-primary" onClick={() => setShowForm(true)}>+ New Proposal</button>
          </div>

          {/* Past proposals */}
          {loadingProposals ? (
            <div className="bb-loading-spinner" />
          ) : proposals.length === 0 ? (
            <div className="bb-empty-state">You haven't submitted any proposals yet.</div>
          ) : (
            <table className="bb-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Proposed Date</th>
                  <th>Status</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {proposals.map(p => {
                  const badge = PROPOSAL_STATUS_BADGE[p.status] || { label: p.status, cls: 'bb-badge bb-badge--grey' };
                  return (
                    <tr key={p.id}>
                      <td><strong>{p.title}</strong></td>
                      <td>{categoryLabel(p.category)}</td>
                      <td>{fmtProposalDate(p.start_datetime)}</td>
                      <td><span className={badge.cls}>{badge.label}</span></td>
                      <td style={{ fontSize: 12, color: '#6B7280' }}>
                        {p.status === 'DECLINED' && p.decline_reason ? p.decline_reason : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}

      {showForm && (
        <div style={{ maxWidth: 640 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ margin: 0 }}>New Event Proposal</h3>
            <button className="bb-btn-outline-sm" onClick={() => { setShowForm(false); setForm(EMPTY_PROPOSAL); }}>Cancel</button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="bb-form-row">
              <div className="bb-form-group">
                <label>Event Title <span style={{ color: '#DC2626' }}>*</span></label>
                <input className="bb-input" required value={form.title} onChange={e => setF('title', e.target.value)} placeholder="E.g. Community Health Talk" />
              </div>
              <div className="bb-form-group">
                <label>Category</label>
                <select className="bb-select" value={form.category} onChange={e => setF('category', e.target.value)}>
                  {EVENT_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            </div>

            <div className="bb-form-row">
              <div className="bb-form-group">
                <label>Start Date & Time <span style={{ color: '#DC2626' }}>*</span></label>
                <input className="bb-input" required type="datetime-local" value={form.start_datetime} onChange={e => setF('start_datetime', e.target.value)} />
              </div>
              <div className="bb-form-group">
                <label>End Date & Time <span style={{ color: '#DC2626' }}>*</span></label>
                <input className="bb-input" required type="datetime-local" value={form.end_datetime} onChange={e => setF('end_datetime', e.target.value)} />
              </div>
            </div>

            <div className="bb-form-row">
              <div className="bb-form-group">
                <label>Location Name</label>
                <input className="bb-input" value={form.location.name} onChange={e => setLoc('name', e.target.value)} placeholder="E.g. Community Hall A" />
              </div>
              <div className="bb-form-group">
                <label>Location Address</label>
                <input className="bb-input" value={form.location.address} onChange={e => setLoc('address', e.target.value)} placeholder="Street address" />
              </div>
            </div>

            <div className="bb-form-row">
              <div className="bb-form-group">
                <label>Member Capacity</label>
                <input className="bb-input" type="number" min={0} value={form.member_capacity} onChange={e => setF('member_capacity', e.target.value)} placeholder="e.g. 30" />
              </div>
              <div className="bb-form-group">
                <label>Volunteer Attendee Capacity</label>
                <input className="bb-input" type="number" min={0} value={form.volunteer_capacity} onChange={e => setF('volunteer_capacity', e.target.value)} placeholder="e.g. 5" />
              </div>
            </div>

            {/* Audience Estimation */}
            <AudienceEstimation orgId={orgId} />

            <div className="bb-form-group">
              <label>Description <span style={{ color: '#DC2626' }}>*</span></label>
              <textarea className="bb-textarea" rows={4} required value={form.description} onChange={e => setF('description', e.target.value)} placeholder="Describe the event, its purpose, and expected outcomes..." />
            </div>

            <div className="bb-form-row">
              <div className="bb-form-group">
                <label>Tags <span style={{ fontSize: 12, color: '#9CA3AF' }}>(comma separated)</span></label>
                <input className="bb-input" value={form.tags} onChange={e => setF('tags', e.target.value)} placeholder="e.g. health, seniors, wellness" />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
              <button className="bb-btn-primary" type="submit" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit Proposal'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

/* ─── Root ───────────────────────────────────────────────────────── */
function MyEventsVolunteer({ orgId, volunteerId, volunteerName, volunteerEmail, activeTab }) {
  const titles = {
    'View Upcoming Events':  'View Upcoming Events',
    'Volunteer for Event':  'Volunteer for Event',
    'Register for Event':   'Register for Event',
    'Propose An Event':     'Propose An Event',
  };

  const subtitles = {
    'Volunteer for Event': 'Browse available events and sign up to volunteer.',
    'Register for Event':  'Browse upcoming events and register as a participant.',
    'Propose An Event':    'Submit an event idea for admin review and approval.',
  };

  return (
    <div className="bb-vol-tool">
      <div className="bb-vol-tool-header">
        <h2>{titles[activeTab] || activeTab}</h2>
        {subtitles[activeTab] && (
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6B7280' }}>{subtitles[activeTab]}</p>
        )}
      </div>
      <div className="bb-vol-section">
        {activeTab === 'View Upcoming Events' && (
          <AllUpcomingEvents orgId={orgId} volunteerId={volunteerId} />
        )}
        {activeTab === 'Volunteer for Event' && (
          <VolunteerOpportunities orgId={orgId} volunteerId={volunteerId} volunteerName={volunteerName} />
        )}
        {activeTab === 'Register for Event' && (
          <RegisterForEvents orgId={orgId} volunteerId={volunteerId} volunteerName={volunteerName} volunteerEmail={volunteerEmail} />
        )}
        {activeTab === 'Propose An Event' && (
          <ProposeEvent orgId={orgId} volunteerId={volunteerId} volunteerName={volunteerName} />
        )}
      </div>
    </div>
  );
}

export default MyEventsVolunteer;
